import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismInput, PrismModal } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import type { CostSettingProfile } from 'lumen/models';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface SettingsCostModalsCreateProfileSignature {
  Args: {
    onClose: () => void;
    onProfileCreated: () => Promise<void>;
    takenNames: string[];
  };
}

export default class SettingsCostModalsCreateProfile extends Component<SettingsCostModalsCreateProfileSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked description = '';
  @tracked hasEditedName = false;
  @tracked name = '';

  createProfileTask = task({ drop: true }, async (): Promise<void> => {
    if (!this.isValid) {
      return;
    }

    const profile = this.store.createRecord<CostSettingProfile>(
      'cost-setting/profile',
      { name: this.trimmedName, description: this.description.trim() },
    );

    try {
      await profile.save();
    } catch (error) {
      // No response to normalize, so clean up the new record here.
      profile.unloadRecord();

      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }

    this.toast.success(this.intl.t('settings.cost.created'), {
      showIcon: true,
    });

    // The edit route looks the profile up in the list, so refresh it first.
    // The route only alerts when the initial load fails, so alert here. The
    // edit route would only bounce back, so stay on the list.
    try {
      await this.args.onProfileCreated();
    } catch {
      void this.alerting.showErrorAlert();
      this.args.onClose();

      return;
    }

    if (profile.id) {
      this.router.transitionTo('market.settings.cost.edit', profile.id);
    } else {
      this.errorTracking.captureException(
        new Error('Created cost profile has no id'),
      );
      void this.alerting.showErrorAlert();
    }

    this.args.onClose();
  });

  get isNameUnique(): boolean {
    const name = this.trimmedName.toLowerCase();

    return !this.args.takenNames.some(
      (takenName) => takenName.trim().toLowerCase() === name,
    );
  }

  get isValid(): boolean {
    return this.trimmedName.length > 0 && this.isNameUnique;
  }

  get nameError(): string | undefined {
    if (this.trimmedName.length > 0 && !this.isNameUnique) {
      return this.intl.t('settings.cost.name.error.not_unique');
    }

    if (this.hasEditedName && this.trimmedName.length === 0) {
      return this.intl.t('settings.cost.name.error.required');
    }

    return undefined;
  }

  get trimmedName(): string {
    return this.name.trim();
  }

  @action updateDescription(event: Event): void {
    this.description = (event.target as HTMLInputElement).value;
  }

  @action updateName(event: Event): void {
    this.hasEditedName = true;
    this.name = (event.target as HTMLInputElement).value;
  }

  <template>
    <PrismModal
      @isCloseDisabled={{this.createProfileTask.isRunning}}
      @onClose={{@onClose}}
      data-test-id="cost-create-profile-modal"
    >
      <:headerContent>
        <h3 class="flex w-full items-center">
          {{t "settings.cost.create_page.title"}}
        </h3>
      </:headerContent>

      <:default>
        <div class="modal--padded modal--small flex flex-col gap-400">
          <PrismInput
            @autofocus={{true}}
            @errorMessage={{this.nameError}}
            @hasErrors={{if this.nameError true false}}
            @isTouched={{if this.nameError true false}}
            @label={{t "general.name"}}
            @onInput={{this.updateName}}
            @removePadding={{true}}
            @type="text"
            @value={{this.name}}
            aria-label={{t "general.name"}}
            data-test-id="cost-name-input"
          />

          <div class="flex flex-col gap-100">
            <div class="flex items-baseline justify-between">
              <span class="paragraph-200--bold">
                {{t "settings.cost.description.label"}}
              </span>
              <span class="text-neutral-subdued text-200--regular">
                {{t "settings.cost.description.optional"}}
              </span>
            </div>

            <PrismInput
              @onInput={{this.updateDescription}}
              @placeholder={{t "settings.cost.description.placeholder"}}
              @removePadding={{true}}
              @type="text"
              @value={{this.description}}
              aria-label={{t "settings.cost.description.label"}}
              data-test-id="cost-description-input"
            />

            <p class="text-neutral-subdued text-200--regular">
              {{t "settings.cost.description.note"}}
            </p>
          </div>
        </div>
      </:default>

      <:footer>
        <div class="settings-modal__footer">
          <GeneralFormFieldsCancelAndConfirm
            @cancel={{@onClose}}
            @confirm={{this.createProfileTask.perform}}
            @confirmText={{t "general.create"}}
            @isCancelDisabled={{this.createProfileTask.isRunning}}
            @isConfirmDisabled={{not this.isValid}}
            @isConfirmLoading={{this.createProfileTask.isRunning}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
